class CartRemoveButton extends HTMLElement {
	constructor() {
		super();

		this.addEventListener('click', (event) => {
			event.preventDefault();
			const cartItems = this.closest('cart-items') || this.closest('cart-drawer-items');
			cartItems.updateQuantity(this.dataset.index, 0);
		});
	}
}

customElements.define('cart-remove-button', CartRemoveButton);

class CartItems extends HTMLElement {
	constructor() {
		super();
		this.lineItemStatusElement =
			document.getElementById('shopping-cart-line-item-status') || document.getElementById('CartDrawer-LineItemStatus');

		const debouncedOnChange = debounce((event) => {
			this.onChange(event);
		}, ON_CHANGE_DEBOUNCE_TIMER);

		this.addEventListener('change', debouncedOnChange.bind(this));

		// Flag to track if an additional product is currently being added
		this.isAddingAdditionalProduct = false;
		// Flag to prevent recursive calls to checkAndAddAdditionalProduct
		this.isCheckingAdditionalProduct = false;
	}

	cartUpdateUnsubscriber = undefined;

	connectedCallback() {
		this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
			if (event.source === 'cart-items') {
				return;
			}
			this.onCartUpdate(event);
		});
	}

	disconnectedCallback() {
		if (this.cartUpdateUnsubscriber) {
			this.cartUpdateUnsubscriber();
		}
	}

	resetQuantityInput(id) {
		const input = this.querySelector(`#Quantity-${id}`);
		input.value = input.getAttribute('value');
		this.isEnterPressed = false;
	}

	setValidity(event, index, message) {
		event.target.setCustomValidity(message);
		event.target.reportValidity();
		this.resetQuantityInput(index);
		event.target.select();
	}

	validateQuantity(event) {
		const inputValue = parseInt(event.target.value);
		const index = event.target.dataset.index;
		let message = '';

		if (inputValue < event.target.dataset.min) {
			message = window.quickOrderListStrings.min_error.replace('[min]', event.target.dataset.min);
		} else if (inputValue > parseInt(event.target.max)) {
			message = window.quickOrderListStrings.max_error.replace('[max]', event.target.max);
		} else if (inputValue % parseInt(event.target.step) !== 0) {
			message = window.quickOrderListStrings.step_error.replace('[step]', event.target.step);
		}

		if (message) {
			this.setValidity(event, index, message);
		} else {
			event.target.setCustomValidity('');
			event.target.reportValidity();
			this.updateQuantity(
				index,
				inputValue,
				document.activeElement.getAttribute('name'),
				event.target.dataset.quantityVariantId
			);
		}
	}

	onChange(event) {
		this.validateQuantity(event);
	}

	async onCartUpdate(event) {
		const skipAdditionalCheck = this.isAddingAdditionalProduct;
		const additionalProductElement = document.querySelector('.additional-product');

		// If we have cart data from the event, use it to update the additional product total
		if (event && event.cartData) {
			if (additionalProductElement) {
				additionalProductElement.dataset.cartTotal = event.cartData.total_price;
			}
		}

		if (this.tagName === 'CART-DRAWER-ITEMS') {
			try {
				const response = await fetch(`${routes.cart_url}?section_id=cart-drawer`);
				const responseText = await response.text();
				const html = new DOMParser().parseFromString(responseText, 'text/html');
				const selectors = ['cart-drawer-items', '.cart-drawer__footer', '.cart__footer'];

				for (const selector of selectors) {
					const targetElement = document.querySelector(selector);
					const sourceElement = html.querySelector(selector);
					if (targetElement && sourceElement) {
						targetElement.replaceWith(sourceElement);
					}
				}

				// Update total if not already updated from event
				if (additionalProductElement && (!event || !event.cartData)) {
					const cartTotalElement = html.querySelector('.totals__subtotal-value');
					if (cartTotalElement) {
						const cartTotalText = cartTotalElement.textContent.trim();
						const cartTotal = parseInt(cartTotalText.replace(/[^0-9]/g, ''));
						additionalProductElement.dataset.cartTotal = cartTotal;
					}
				}
			} catch (e) {
				console.error(e);
			}
		} else {
			try {
				const response = await fetch(`${routes.cart_url}?section_id=main-cart-items`);
				const responseText = await response.text();
				const html = new DOMParser().parseFromString(responseText, 'text/html');
				const sourceQty = html.querySelector('cart-items');
				this.innerHTML = sourceQty.innerHTML;

				const totals = document.querySelector('cart-items').dataset.cartTotal;
				const count = document.querySelector('cart-items').dataset.quantity;
				const countElem = document.querySelector('#cart-icon-bubble .cart-count-bubble > span');
				const cartTotalElem = document.querySelector('.cart__blocks .totals__total-value');

				if (cartTotalElem) cartTotalElem.textContent = totals;
				if (countElem) countElem.textContent = count;

				// Update total if not already updated from event
				if (additionalProductElement && (!event || !event.cartData)) {
					const cartTotalElement = html.querySelector('.totals__subtotal-value');
					if (cartTotalElement) {
						const cartTotalText = cartTotalElement.textContent.trim();
						const cartTotal = parseInt(cartTotalText.replace(/[^0-9]/g, ''));
						additionalProductElement.dataset.cartTotal = cartTotal;
					}
				}
			} catch (e) {
				console.error(e);
			}
		}

		// Check for additional product
		if (!skipAdditionalCheck && additionalProductElement) {
			// Pass the cart data if available to avoid refetching
			this.checkAndAddAdditionalProduct(event ? event.cartData : null);
		}
	}

	getSectionsToRender() {
		return [
			{
				id: 'main-cart-items',
				section: document.getElementById('main-cart-items').dataset.id,
				selector: '.js-contents',
			},
			{
				id: 'cart-icon-bubble',
				section: 'cart-icon-bubble',
				selector: null,
			},
			{
				id: 'cart-live-region-text',
				section: 'cart-live-region-text',
				selector: null,
			},
			{
				id: 'main-cart-footer',
				section: document.getElementById('main-cart-footer').dataset.id,
				selector: '#main-cart-footer',
			},
		];
	}

	updateQuantity(line, quantity, name, variantId) {
		this.enableLoading(line);

		const body = JSON.stringify({
			line,
			quantity,
			sections: this.getSectionsToRender().map((section) => section.section),
			sections_url: window.location.pathname,
		});

		fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body } })
			.then((response) => {
				return response.text();
			})
			.then((state) => {
				const parsedState = JSON.parse(state);
				const quantityElement =
					document.getElementById(`Quantity-${line}`) || document.getElementById(`Drawer-quantity-${line}`);
				const items = document.querySelectorAll('.cart-item');

				if (parsedState.errors) {
					quantityElement.value = quantityElement.getAttribute('value');
					this.updateLiveRegions(line, parsedState.errors);
					return;
				}

				this.classList.toggle('is-empty', parsedState.item_count === 0);
				const cartDrawerWrapper = document.querySelector('cart-drawer');
				const cartFooter = document.getElementById('main-cart-footer');

				if (cartFooter) cartFooter.classList.toggle('is-empty', parsedState.item_count === 0);
				if (cartDrawerWrapper) cartDrawerWrapper.classList.toggle('is-empty', parsedState.item_count === 0);

				this.getSectionsToRender().forEach((section) => {
					const elementToReplace =
						document.getElementById(section.id).querySelector(section.selector) || document.getElementById(section.id);
					elementToReplace.innerHTML = this.getSectionInnerHTML(
						parsedState.sections[section.section],
						section.selector
					);
				});
				const updatedValue = parsedState.items[line - 1] ? parsedState.items[line - 1].quantity : undefined;
				let message = '';
				if (items.length === parsedState.items.length && updatedValue !== parseInt(quantityElement.value)) {
					if (typeof updatedValue === 'undefined') {
						message = window.cartStrings.error;
					} else {
						message = window.cartStrings.quantityError.replace('[quantity]', updatedValue);
					}
				}
				this.updateLiveRegions(line, message);

				const lineItem =
					document.getElementById(`CartItem-${line}`) || document.getElementById(`CartDrawer-Item-${line}`);
				if (lineItem && lineItem.querySelector(`[name="${name}"]`)) {
					cartDrawerWrapper
						? trapFocus(cartDrawerWrapper, lineItem.querySelector(`[name="${name}"]`))
						: lineItem.querySelector(`[name="${name}"]`).focus();
				} else if (parsedState.item_count === 0 && cartDrawerWrapper) {
					trapFocus(cartDrawerWrapper.querySelector('.drawer__inner-empty'), cartDrawerWrapper.querySelector('a'));
				} else if (document.querySelector('.cart-item') && cartDrawerWrapper) {
					trapFocus(cartDrawerWrapper, document.querySelector('.cart-item__name'));
				}

				// Update the data-cart-total attribute with the latest cart total
				const additionalProductElement = document.querySelector('.additional-product');
				if (additionalProductElement) {
					additionalProductElement.dataset.cartTotal = parsedState.total_price;

					// Check if we need to add or remove the additional product
					// Skip if we're already in the process of adding an additional product
					if (!this.isAddingAdditionalProduct) {
						this.checkAndAddAdditionalProduct(parsedState);
					} else {
						console.log('Skipping additional product check during quantity update from add operation');
					}
				}

				publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: parsedState, variantId: variantId });
			})
			.catch(() => {
				this.querySelectorAll('.loading__spinner').forEach((overlay) => overlay.classList.add('hidden'));
				const errors = document.getElementById('cart-errors') || document.getElementById('CartDrawer-CartErrors');
				errors.textContent = window.cartStrings.error;
			})
			.finally(() => {
				this.disableLoading(line);
			});
	}

	updateLiveRegions(line, message) {
		const lineItemError =
			document.getElementById(`Line-item-error-${line}`) || document.getElementById(`CartDrawer-LineItemError-${line}`);
		if (lineItemError) lineItemError.querySelector('.cart-item__error-text').textContent = message;

		this.lineItemStatusElement.setAttribute('aria-hidden', true);

		const cartStatus =
			document.getElementById('cart-live-region-text') || document.getElementById('CartDrawer-LiveRegionText');
		cartStatus.setAttribute('aria-hidden', false);

		setTimeout(() => {
			cartStatus.setAttribute('aria-hidden', true);
		}, 1000);
	}

	getSectionInnerHTML(html, selector) {
		const effectiveSelector = selector || '.shopify-section';
		const parsedHtml = new DOMParser().parseFromString(html, 'text/html');
		const element = parsedHtml.querySelector(effectiveSelector);
		if (!element) {
			console.error('Element not found in HTML with selector:', effectiveSelector);
			console.log('HTML snippet:', html.substring(0, 100) + '...');
			return '';
		}
		return element.innerHTML;
	}

	enableLoading(line) {
		const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
		mainCartItems.classList.add('cart__items--disabled');

		const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading__spinner`);
		const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading__spinner`);

		[...cartItemElements, ...cartDrawerItemElements].forEach((overlay) => overlay.classList.remove('hidden'));

		document.activeElement.blur();
		this.lineItemStatusElement.setAttribute('aria-hidden', false);
	}

	disableLoading(line) {
		const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
		mainCartItems.classList.remove('cart__items--disabled');

		const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading__spinner`);
		const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading__spinner`);

		cartItemElements.forEach((overlay) => overlay.classList.add('hidden'));
		cartDrawerItemElements.forEach((overlay) => overlay.classList.add('hidden'));
	}

	async checkAndAddAdditionalProduct(cartData = null) {
		if (this.isCheckingAdditionalProduct) return;
		if (this.isAddingAdditionalProduct) return;

		this.isCheckingAdditionalProduct = true;

		try {
			const additionalProductElement = document.querySelector('.additional-product');
			if (!additionalProductElement) return;

			const standardProductId = additionalProductElement.dataset.product;
			const cooledProductId = additionalProductElement.dataset.productCooled;
			const minimumTotal = parseInt(additionalProductElement.dataset.total);

			console.log('Additional Product Configuration:', {
				standardProductId,
				cooledProductId,
				minimumTotal
			});

			if (!standardProductId && !cooledProductId) {
				console.log('No additional products configured');
				return;
			}

			// Use provided cartData or fetch it
			let cart = cartData;
			if (!cart) {
				const res = await fetch(`${routes.cart_url}.js`, { headers: { 'Accept': 'application/json' } });
				cart = await res.json();
			}

			const items = cart.items || [];
			console.log('Cart Items:', items);

			const standardItem = standardProductId ? items.find((it) => String(it.id) === String(standardProductId)) : null;
			const cooledItem = cooledProductId ? items.find((it) => String(it.id) === String(cooledProductId)) : null;

			const standardExists = !!standardItem;
			const cooledExists = !!cooledItem;

			const standardPrice = standardItem ? parseInt(standardItem.price) : 0;
			const cooledPrice = cooledItem ? parseInt(cooledItem.price) : 0;

			// Check for digital-only cart (excluding the additional product itself)
			const otherItems = items.filter((it) => String(it.id) !== String(standardProductId) && String(it.id) !== String(cooledProductId));
			console.log('Other Items:', otherItems);

			const isOnlyDigital = otherItems.length > 0 && otherItems.every((it) => !it.requires_shipping);

			if (isOnlyDigital) {
				console.log('Cart is digital only');
				const toRemove = [];
				if (standardExists) toRemove.push(standardProductId);
				if (cooledExists) toRemove.push(cooledProductId);
				if (toRemove.length > 0) await this.removeAdditionalProducts(toRemove);
				return;
			}

			// Enforce quantity 1
			if (standardItem && standardItem.quantity > 1) {
				await this.updateQuantityByProductId(standardProductId, 1);
				return;
			}
			if (cooledItem && cooledItem.quantity > 1) {
				await this.updateQuantityByProductId(cooledProductId, 1);
				return;
			}

			const hasCooledProducts = otherItems.some(it => {
				const type = it.product_type ? it.product_type.toLowerCase().trim() : '';
				console.log(`Item ${it.title} type: ${it.product_type} (normalized: ${type})`);
				return type === 'cooled';
			});
			const hasOtherProducts = otherItems.some(it => !it.product_type || it.product_type.toLowerCase().trim() !== 'cooled');

			console.log('Has Cooled Products:', hasCooledProducts);
			console.log('Has Other Products:', hasOtherProducts);

			const cartTotalWithoutAdditional = parseInt(cart.total_price) - standardPrice - cooledPrice;
			console.log('Cart Total (net):', cartTotalWithoutAdditional, 'Minimum:', minimumTotal);

			const itemsToAdd = [];
			const itemsToRemove = [];

			// 1. Cooled Logic
			if (cooledProductId) {
				const shouldAddCooled = hasCooledProducts && (cartTotalWithoutAdditional > minimumTotal);
				console.log('Should Add Cooled:', shouldAddCooled);
				if (shouldAddCooled) {
					if (!cooledExists) {
						console.log('Adding Cooled Product:', cooledProductId);
						itemsToAdd.push(cooledProductId);
					}
				} else {
					if (cooledExists) {
						console.log('Removing Cooled Product:', cooledProductId);
						itemsToRemove.push(cooledProductId);
					}
				}
			}

			// 2. Standard Logic
			if (standardProductId) {
				const shouldAddStandard = hasOtherProducts && (cartTotalWithoutAdditional > minimumTotal);
				console.log('Should Add Standard:', shouldAddStandard);
				if (shouldAddStandard) {
					if (!standardExists) {
						console.log('Adding Standard Product:', standardProductId);
						itemsToAdd.push(standardProductId);
					}
				} else {
					if (standardExists) {
						console.log('Removing Standard Product:', standardProductId);
						itemsToRemove.push(standardProductId);
					}
				}
			}

			if (itemsToRemove.length > 0) {
				await this.removeAdditionalProducts(itemsToRemove);
			}

			if (itemsToAdd.length > 0) {
				await this.addAdditionalProducts(itemsToAdd);
			}

		} catch (e) {
			console.error('Error in checkAndAddAdditionalProduct:', e);
		} finally {
			this.isCheckingAdditionalProduct = false;
		}
	}

	async removeAdditionalProducts(productIds) {
		if (!productIds || productIds.length === 0 || this.isAddingAdditionalProduct) return;

		const linesToDisable = [];
		const cartItems = this.querySelectorAll('.cart-item');
		cartItems.forEach((item, index) => {
			if (productIds.includes(item.dataset.additionalProductId)) {
				const line = index + 1;
				this.enableLoading(line);
				linesToDisable.push(line);
			}
		});

		const updates = {};
		productIds.forEach((id) => {
			updates[id] = 0;
		});

		const body = JSON.stringify({
			updates: updates,
			sections: this.getSectionsToRender().map((section) => section.section),
			sections_url: window.location.pathname,
		});

		fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } })
			.then((response) => {
				return response.text();
			})
			.then((state) => {
				const parsedState = JSON.parse(state);

				if (parsedState.errors) {
					return;
				}

				this.classList.toggle('is-empty', parsedState.item_count === 0);
				const cartDrawerWrapper = document.querySelector('cart-drawer');
				const cartFooter = document.getElementById('main-cart-footer');

				if (cartFooter) cartFooter.classList.toggle('is-empty', parsedState.item_count === 0);
				if (cartDrawerWrapper) cartDrawerWrapper.classList.toggle('is-empty', parsedState.item_count === 0);

				this.getSectionsToRender().forEach((section) => {
					const elementToReplace =
						document.getElementById(section.id).querySelector(section.selector) || document.getElementById(section.id);
					elementToReplace.innerHTML = this.getSectionInnerHTML(
						parsedState.sections[section.section],
						section.selector
					);
				});

				const additionalProductElement = document.querySelector('.additional-product');
				if (additionalProductElement) {
					additionalProductElement.dataset.cartTotal = parsedState.total_price;

					if (!this.isAddingAdditionalProduct) {
						this.checkAndAddAdditionalProduct(parsedState);
					}
				}

				publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: parsedState });
			})
			.catch(() => {
				this.querySelectorAll('.loading__spinner').forEach((overlay) => overlay.classList.add('hidden'));
				const errors = document.getElementById('cart-errors') || document.getElementById('CartDrawer-CartErrors');
				errors.textContent = window.cartStrings.error;
			})
			.finally(() => {
				linesToDisable.forEach((line) => this.disableLoading(line));
			});
	}

	async updateQuantityByProductId(productId, quantity) {
		const cartItemsDom = this.querySelectorAll('.cart-item');
		let lineItemIndex = null;
		cartItemsDom.forEach((item, index) => {
			if (item.dataset.additionalProductId === String(productId)) {
				lineItemIndex = index + 1;
			}
		});
		if (lineItemIndex !== null) {
			this.updateQuantity(lineItemIndex, quantity);
		}
	}

	async addAdditionalProducts(productIds) {
		if (!productIds || productIds.length === 0 || this.isAddingAdditionalProduct) return;

		this.isAddingAdditionalProduct = true;

		try {
			const items = productIds.map(id => ({
				id: parseInt(id),
				quantity: 1
			}));

			const formData = {
				'items': items,
				'sections': this.getSectionsToRender().map((section) => section.section)
			};

			const response = await fetch(`${routes.cart_add_url}`, {
				method: 'POST',
				headers: {
					'X-Requested-With': 'XMLHttpRequest',
					'Content-Type': 'application/json'
				},
				body: JSON.stringify(formData)
			});

			if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);

			const data = await response.json();

			if (data.sections) {
				if (document.querySelector('cart-drawer')) {
					document.querySelector('cart-drawer').renderContents(data);
				} else {
					this.classList.toggle('is-empty', data.item_count === 0);
					const cartDrawerWrapper = document.querySelector('cart-drawer');
					const cartFooter = document.getElementById('main-cart-footer');

					if (cartFooter) cartFooter.classList.toggle('is-empty', data.item_count === 0);
					if (cartDrawerWrapper) cartDrawerWrapper.classList.toggle('is-empty', data.item_count === 0);

					this.getSectionsToRender().forEach((section) => {
						const elementToReplace = section.selector
							? document.getElementById(section.id).querySelector(section.selector)
							: document.getElementById(section.id);

						if (elementToReplace && data.sections[section.section]) {
							elementToReplace.innerHTML = this.getSectionInnerHTML(
								data.sections[section.section],
								section.selector
							);
						}
					});

					const additionalProductElement = document.querySelector('.additional-product');
					if (additionalProductElement) {
						additionalProductElement.dataset.cartTotal = data.total_price;
					}
				}
			}
		} catch (error) {
			console.error('Error adding product to cart:', error);
			this.onCartUpdate();
		} finally {
			this.isAddingAdditionalProduct = false;
		}
	}

	async removeAdditionalProduct(productId) {
		if (!productId || this.isAddingAdditionalProduct) return;

		const cartItems = this.querySelectorAll('.cart-item');
		let lineItemIndex = null;

		cartItems.forEach((item, index) => {
			if (item.dataset.additionalProductId === String(productId)) {
				lineItemIndex = index + 1;
			}
		});

		if (lineItemIndex !== null) {
			this.updateQuantity(lineItemIndex, 0);
		}
	}
}

customElements.define('cart-items', CartItems);

if (!customElements.get('cart-note')) {
	customElements.define(
		'cart-note',
		class CartNote extends HTMLElement {
			constructor() {
				super();

				this.addEventListener(
					'input',
					debounce((event) => {
						const body = JSON.stringify({ note: event.target.value });
						fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } });
					}, ON_CHANGE_DEBOUNCE_TIMER)
				);
			}
		}
	);
}

